<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useSdk } from '@/composables/useSdk';
import type { Post } from '@/types';
import type { UserProfile } from '@cc-consulting-nv/ccsdk';

interface Props {
  track: Post | null;
  size?: 'mini' | 'compact' | 'default' | 'large';
  layout?: 'horizontal' | 'vertical';
  artworkSize?: number | string;
  clickable?: boolean;
  isPlayingAd?: boolean;
  adInfo?: { title?: string } | null;
}

const props = withDefaults(defineProps<Props>(), {
  track: null,
  size: 'default',
  layout: 'horizontal',
  artworkSize: undefined,
  clickable: false,
  isPlayingAd: false,
  adInfo: null,
});

const emit = defineEmits<{
  (e: 'click'): void;
  (e: 'artwork-click'): void;
}>();

const { sdk } = useSdk();
const cachedUserProfile = ref<UserProfile | null>(null);
const isLoadingProfile = ref(false);

// Helper to get track property
function getTrackProp<T>(key: string): T | undefined {
  if (!props.track) return undefined;
  return (props.track as Record<string, unknown>)[key] as T | undefined;
}

// Get user ID from current track
const trackUserId = computed(() => {
  if (!props.track) return undefined;

  if (props.track.userId) {
    return props.track.userId;
  }

  const userId = getTrackProp<string>('user_ulid') || getTrackProp<string>('userULID');
  const userObj = getTrackProp<{ ulid?: string; userId?: string }>('user');

  return userId || userObj?.ulid || userObj?.userId;
});

// Fetch user profile for artist name
async function fetchUserProfile() {
  const userId = trackUserId.value;

  if (!userId || !sdk) {
    isLoadingProfile.value = false;
    return;
  }

  isLoadingProfile.value = true;

  try {
    const profile = await sdk.fetchUserProfileById(userId);
    if (profile) {
      cachedUserProfile.value = profile;
    }
  } catch (error) {
    console.error('[TrackInfo] Failed to fetch user profile:', error);
  } finally {
    isLoadingProfile.value = false;
  }
}

// Watch for track changes to fetch user profile
watch(
  () => props.track?.ulid,
  () => {
    cachedUserProfile.value = null;
    fetchUserProfile();
  },
  { immediate: true }
);

// Computed properties
const artworkUrl = computed(() => {
  if (!props.track) return null;
  const images = props.track.images as Array<{ url?: string }> | undefined;
  return images?.[0]?.url || null;
});

const displayArtist = computed(() => {
  if (props.isPlayingAd) return 'Sponsored';
  if (!props.track) return 'Unknown Artist';

  // Check direct artist field
  if (props.track.artist) return props.track.artist;

  // Check alternative field names
  const artistName = getTrackProp<string>('artist_name') || getTrackProp<string>('artistName');
  if (artistName) return artistName;

  // Check embedded user data
  const user = getTrackProp<{ name?: string; displayName?: string }>('user');
  if (user) {
    if (user.displayName) return user.displayName;
    if (user.name) return user.name;
  }

  // Check flat user fields
  const userName = getTrackProp<string>('userName') || getTrackProp<string>('user_name');
  if (userName) return userName;

  // Show loading state
  if (isLoadingProfile.value) return 'Loading...';

  // Check cached user profile
  if (cachedUserProfile.value) {
    const profile = cachedUserProfile.value;
    if (profile.displayName) return profile.displayName;
    const profileName = (profile as Record<string, unknown>).name as string | undefined;
    if (profileName) return profileName;
  }

  return 'Unknown Artist';
});

const displayTitle = computed((): string => {
  if (props.isPlayingAd && props.adInfo) {
    return props.adInfo.title || 'Advertisement';
  }
  if (!props.track) return 'No track playing';
  const title = props.track.title;
  const name = (props.track as Record<string, unknown>).name as string | undefined;
  return (typeof title === 'string' ? title : '') || name || 'Untitled';
});

// Computed sizes
const computedArtworkSize = computed(() => {
  if (props.artworkSize) return props.artworkSize;
  switch (props.size) {
    case 'mini':
      return 32;
    case 'compact':
      return 48;
    case 'large':
      return 400;
    default:
      return 56;
  }
});

const placeholderIconSize = computed(() => {
  switch (props.size) {
    case 'mini':
      return 16;
    case 'compact':
      return 24;
    case 'large':
      return 120;
    default:
      return 28;
  }
});

function handleClick() {
  if (props.clickable && !props.isPlayingAd) {
    emit('click');
  }
}

function handleArtworkClick() {
  if (props.clickable && !props.isPlayingAd) {
    emit('artwork-click');
  }
}
</script>

<template>
  <div
    class="track-info"
    :class="[
      `track-info--${size}`,
      `track-info--${layout}`,
      { 'track-info--clickable': clickable && !isPlayingAd }
    ]"
    @click="handleClick"
  >
    <!-- Artwork -->
    <div
      class="track-info__artwork"
      :class="{ 'track-info__artwork--clickable': clickable && !isPlayingAd }"
      :style="{
        width: typeof computedArtworkSize === 'number' ? `${computedArtworkSize}px` : computedArtworkSize,
        height: typeof computedArtworkSize === 'number' ? `${computedArtworkSize}px` : computedArtworkSize,
      }"
      :role="clickable && !isPlayingAd ? 'button' : undefined"
      :tabindex="clickable && !isPlayingAd ? 0 : undefined"
      @click.stop="handleArtworkClick"
      @keydown.enter.stop="handleArtworkClick"
    >
      <!-- Ad indicator badge -->
      <v-chip
        v-if="isPlayingAd"
        class="track-info__ad-badge"
        color="warning"
        size="x-small"
        label
      >
        AD
      </v-chip>

      <!-- Ad artwork placeholder -->
      <div v-if="isPlayingAd" class="track-info__ad-artwork">
        <v-icon :size="placeholderIconSize" color="warning">mdi-advertisements</v-icon>
      </div>

      <!-- Normal artwork -->
      <img
        v-else-if="artworkUrl"
        :src="artworkUrl"
        :alt="displayTitle"
        class="track-info__artwork-img"
      />

      <!-- Placeholder -->
      <div v-else class="track-info__artwork-placeholder">
        <v-icon :size="placeholderIconSize" color="grey">mdi-music-note</v-icon>
      </div>
    </div>

    <!-- Text Info -->
    <div class="track-info__text">
      <span
        class="track-info__title"
        :class="{
          'track-info__title--clickable': clickable && !isPlayingAd,
          'track-info__title--ad': isPlayingAd
        }"
      >
        {{ displayTitle }}
      </span>
      <span
        class="track-info__artist"
        :class="{ 'track-info__artist--ad': isPlayingAd }"
      >
        {{ displayArtist }}
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.track-info {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  &--vertical {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }

  &--horizontal {
    flex-direction: row;
    text-align: left;
  }

  &--clickable {
    cursor: pointer;
  }

  // Size variants
  &--mini {
    gap: 8px;

    .track-info__title {
      font-size: 0.875rem;
    }

    .track-info__artist {
      font-size: 0.75rem;
    }
  }

  &--compact {
    gap: 10px;

    .track-info__title {
      font-size: 0.875rem;
      font-weight: 500;
    }

    .track-info__artist {
      font-size: 0.75rem;
    }
  }

  &--large {
    gap: 32px;

    .track-info__title {
      font-size: 1.5rem;
      font-weight: 600;
      color: white;
    }

    .track-info__artist {
      font-size: 1rem;
      color: rgba(255, 255, 255, 0.7);
    }
  }

  &__artwork {
    flex-shrink: 0;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(var(--v-theme-surface-variant), 0.3);
    position: relative;

    &--clickable {
      cursor: pointer;

      &:hover {
        opacity: 0.9;
      }
    }
  }

  &__artwork-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__artwork-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(var(--v-theme-surface-variant), 0.5);
  }

  &__ad-badge {
    position: absolute;
    top: 4px;
    left: 4px;
    z-index: var(--z-base-overlay);
  }

  &__ad-artwork {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(var(--v-theme-warning), 0.1);
  }

  &__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 2px;
  }

  &__title {
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &--clickable {
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }

    &--ad {
      color: rgb(var(--v-theme-warning));
    }
  }

  &__artist {
    font-size: 0.875rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &--ad {
      color: rgba(var(--v-theme-warning), 0.7);
    }
  }
}

// Large size specific styles for full-screen player
.track-info--large {
  .track-info__artwork {
    width: 100%;
    max-width: 400px;
    aspect-ratio: 1;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  }

  .track-info__text {
    width: 100%;
    max-width: 500px;
    gap: 8px;
  }

  .track-info__title {
    line-height: 1.3;
    margin: 0;
  }

  .track-info__artist {
    line-height: 1.4;
    margin: 0;
  }

  .track-info__artwork-placeholder {
    background: rgba(255, 255, 255, 0.1);
  }
}
</style>
